import React, { ReactNode } from "react"
import {
  Button,
  Price,
  Heading,
  Paragraph,
  Badge,
} from "@gorilla-technologies/customer-ui"
import { FaArrowRight } from "react-icons/fa"
import { ProductImagePlaceholder } from ".."
import { Product } from "../types.d"

interface ProductDetailsProps {
  product: Product
  image: ReactNode
  onProductPurchase: () => void
  isWalletConnected: boolean
  isWalletLoading: boolean
  connectWallet: () => void
  ownedByUser: boolean
  outOfStock: boolean
  isFetchingProducts: boolean
}

const isSupportedProductType = (productType: string) =>
  productType?.startsWith("image/") ||
  productType?.startsWith("audio/") ||
  productType?.startsWith("video/")

const getBuyButtonLabel = (
  isWalletLoading: boolean,
  isWalletConnected: boolean,
  isFetchingProducts: boolean
) => {
  if (isWalletLoading || isFetchingProducts) return "Loading..."
  if (isWalletConnected) return "Buy now"
  return "Connect Your Wallet to Buy"
}

export const ProductDetails = ({
  product,
  image,
  onProductPurchase,
  isWalletConnected,
  isWalletLoading,
  connectWallet,
  ownedByUser,
  outOfStock,
  isFetchingProducts,
}: ProductDetailsProps) => {
  const { title, description, url, price, productType, mintbaseThingId } =
    product

  return (
    <article className="bg-brand-100 flex w-full flex-col items-center">
      {/* TODO extract component to display NFTs */}
      <div
        className="max-w-8xl flex w-full flex-col items-center gap-8 py-10 px-5 transition ease-linear lg:py-20 lg:px-14"
        id="image-wrapper"
      >
        {!productType?.startsWith("video/") &&
          (image || <ProductImagePlaceholder productType={productType} />)}
        {productType?.startsWith("video/") && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video className="w-full" controls>
            <source src={url} />
          </video>
        )}
        {productType?.startsWith("audio/") && (
          //  eslint-disable-next-line jsx-a11y/media-has-caption
          <audio controls className="audio-player">
            <source src={url} />
          </audio>
        )}
        {!isSupportedProductType(productType) && (
          <a
            href={`https://testnet.mintbase.io/thing/${mintbaseThingId}`}
            className="flex items-center gap-3 text-sm font-medium text-gray-900 underline underline-offset-2 antialiased hover:translate-x-[2px] hover:gap-4 hover:underline md:text-base"
          >
            Preview on mintbase.io
            <FaArrowRight color="#111827" />
          </a>
        )}
      </div>
      <div className="flex w-full justify-center bg-white">
        <div className="max-w-8xl flex w-full flex-col gap-12 py-10 px-5 md:flex-row lg:py-16 lg:px-14">
          <div className="flex grow flex-col gap-4">
            <Heading level={1}>{title}</Heading>
            <Paragraph level={1}>{description}</Paragraph>
          </div>
          <div className="flex flex-col justify-between gap-11 md:items-end">
            <Price value={price?.inNear} size="medium" />
            <div className="flex flex-wrap gap-4">
              {/* TODO Add "Sold Out!" Badge if product is sold out */}
              {ownedByUser && <Badge title="You own this item!" />}
              {outOfStock && <Badge title="Out of Stock" />}
            </div>
            <Button
              title={getBuyButtonLabel(
                isWalletLoading,
                isWalletConnected,
                isFetchingProducts
              )}
              disabled={outOfStock || isFetchingProducts}
              onClick={isWalletConnected ? onProductPurchase : connectWallet}
            />
          </div>
        </div>
      </div>
    </article>
  )
}
